import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { parseAllowedParentOrigin } from "../components/utils";
import { validateTheme } from "../theme/validateTheme";
import { trackEvent } from "../utils/analytics";
import { getHttpErrorMessage } from "../utils/httpErrorMessage";

const baseUrl = process.env.REACT_APP_BASE_URL;

const initialState = {
  verification: null,
  error: null,
  loading: false,
  cameraPermission: "",
  isMobileDevice: true,
  uploading: "",
  closeInitiated: false,
  uploadError: null,
  supportedCountries: null,
  documentTypes: null,
  credentials: null,
  governmentDatabases: null,
  orgTheme: null,
};

export const verificationSlice = createSlice({
  name: "verification",
  initialState,
  reducers: {
    setVerification: (state, action) => {
      state.verification = action.payload;
      state.loading = false;
    },
    setSupportedCountries: (state, action) => {
      state.supportedCountries = action.payload;
      state.loading = false;
    },
    setDocumentTypes: (state, action) => {
      state.documentTypes = action.payload;
      state.loading = false;
    },
    setGovernmentDatabases: (state, action) => {
      state.governmentDatabases = action.payload;
      state.loading = false;
    },
    setCameraPermission: (state, action) => {
      state.cameraPermission = action.payload;
      state.loading = false;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    setUploadError: (state, action) => {
      state.uploadError = action.payload;
      state.uploading = false;
    },
    isLoading: (state) => {
      state.loading = true;
    },
    // Dedicated end-of-initialize signal — deliberately not folded into
    // setCredentials (also dispatched by the 401-refresh interceptor, which
    // must not start clearing `loading` as a side effect) or setOrgTheme (a
    // pure data-setter used from nowhere else — keep it that way).
    initializeComplete: (state) => {
      state.loading = false;
    },
    setCredentials: (state, action) => {
      state.credentials = action.payload;
    },
    setUploading: (state, action) => {
      state.uploading = action.payload;
    },
    setIsMobileDevice: (state, action) => {
      state.isMobileDevice = action.payload;
      state.loading = false;
    },
    setCloseInitiated: (state, action) => {
      state.closeInitiated = action.payload;
    },
    setOrgTheme: (state, action) => {
      state.orgTheme = action.payload;
    },
  },
});

export const {
  setVerification,
  setError,
  isLoading,
  initializeComplete,
  setUploading,
  setUploadError,
  setCameraPermission,
  setCloseInitiated,
  setIsMobileDevice,
  setDocumentTypes,
  setSupportedCountries,
  setCredentials,
  setGovernmentDatabases,
  setOrgTheme,
} = verificationSlice.actions;

/**
 * Tells the embedder and our analytics when core refused a verification for billing (402), at start
 * or at submit. Status-mapped only for the person being verified (getHttpErrorMessage): core's
 * billing-block reasons (TRIAL_EXPIRED, QUOTA_EXCEEDED, ...) are about the org, not them, so the
 * response body is never shown. The embedder is the org, though, so it's told why - with the same
 * error type core records on a check skipped for lack of credits. Does nothing for other errors.
 */
export const reportBillingRefusal = (err, flowId) => (dispatch) => {
  if (err?.response?.status !== 402) {
    return;
  }
  // Our own analytics, so core's reason (CREDITS_REQUIRED, QUOTA_EXCEEDED, ...) is kept.
  trackEvent("verification_unavailable", {
    flowId,
    reason: "insufficient-balance",
    code: err.response.data?.code ?? "unknown",
  });
  dispatch(
    sendEvent({
      type: "verificationError",
      details: { error: "insufficient-balance" },
    })
  );
};

export const startVerification =
  (flowId, metadata, clientId, verificationId, redirectUrl, accessToken) =>
  (dispatch) => {
    dispatch(isLoading());
    return axios
      .post(
        `${baseUrl}/api/v1/verifications`,
        {
          flowId: flowId,
          metadata: metadata,
          verificationId: verificationId,
        },
        {
          headers: {
            "X-CLIENT-ID": clientId,
            "Authorization": `Bearer ${accessToken}`,
          },
        }
      )
      .then((response) => {
        dispatch(setVerification(response.data));
        dispatch(
          sendEvent({
            type: "verificationStarted",
            details: {
              verificationId: response.data.verificationId,
              identityId: response.data.identityId,
            },
          })
        );
        trackEvent("verification_started", {
          verificationId: response.data.verificationId,
          identityId: response.data.identityId,
          flowId,
        });
        dispatch(validateInput(response.data));
        dispatch(validateRedirectUrl(response.data, redirectUrl));
        dispatch(checkMobileDevice());
        dispatch(checkPermission(response.data));
      })
      .catch((err) => {
        trackEvent("verification_start_failed", { flowId, error: err + "" });
        dispatch(reportBillingRefusal(err, flowId));
        dispatch(setError(getHttpErrorMessage(err)));
      });
  };

export const sendMobileEmail = (
  email,
  link,
  verificationId,
  clientId,
  accessToken
) => {
  return axios.post(
    `${baseUrl}/api/v1/verifications/send-mobile-email`,
    {
      emailAddress: email,
      verificationLink: link,
      verificationId: verificationId,
    },
    {
      headers: {
        "X-CLIENT-ID": clientId,
        "Authorization": `Bearer ${accessToken}`,
      },
    }
  );
};

export const getVerificationStatus = (
  verificationId,
  clientId,
  accessToken
) => {
  return axios.get(`${baseUrl}/api/v1/verifications/${verificationId}/status`, {
    headers: {
      "X-CLIENT-ID": clientId,
      "Authorization": `Bearer ${accessToken}`,
    },
  });
};

/**
 * Generate a random nonce for security
 * @private
 */
function _generateNonce() {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  );
}

/**
 * Auth (keys/exchange) and theme (fully public, by flowId) fire in
 * parallel — neither waits on the other, and `loading` only clears once
 * both have settled. Auth failure is fatal (setError); theme failure is
 * swallowed inside fetchBrandingTheme itself (it's cosmetic, non-fatal),
 * so it never rejects the Promise.all below.
 */
export const initialize = (flowId, clientId) => (dispatch) => {
  dispatch(isLoading());
  if (flowId == null || clientId == null) {
    trackEvent("verification_init_failed", {
      flowId,
      error: "Parameters flowId and clientId are required",
    });
    dispatch(setError("Parameters flowId and clientId are required"));
    return;
  }

  trackEvent("sdk_initialized", { flowId, clientId });

  const authPromise = authenticate(clientId).then((response) => {
    dispatch(setCredentials(response.data));
  });
  const themePromise = dispatch(fetchBrandingTheme(flowId));

  Promise.all([authPromise, themePromise])
    .then(() => {
      dispatch(initializeComplete());
    })
    .catch((err) => {
      trackEvent("verification_init_failed", { flowId, error: err + "" });
      dispatch(setError(getHttpErrorMessage(err)));
    });
};

/**
 * Org-wide default theme (colors/logo/language) — the lowest-precedence
 * layer in ThemeProvider's merge (a `theme` query-param override always
 * wins over this). Fully public/unauthenticated (keyed only by flowId, same
 * trust model as the public logo route) so it can run independently of the
 * auth exchange — no clientId/token needed. Failure here is non-fatal:
 * branding is cosmetic, so a failed fetch just leaves orgTheme unset and the
 * app falls back to the hardcoded defaults rather than blocking the
 * verification flow with a hard error state.
 */
export const fetchBrandingTheme = (flowId) => (dispatch) => {
  return axios
    .get(`${baseUrl}/api/v1/public/branding/theme/${flowId}`)
    .then((response) => {
      // Unset fields come back as JSON null (org has no branding configured
      // yet), not omitted — validateTheme() strips those rather than letting
      // them clobber ThemeProvider's DEFAULT_THEME via the merge spread.
      dispatch(setOrgTheme(validateTheme(response.data)));
    })
    .catch((err) => {
      console.error("Failed to fetch org branding theme", err);
    });
};

const authenticate = (clientId) => {
  const timestamp = Date.now().toString();
  const nonce = _generateNonce();
  return axios.post(
    `${baseUrl}/api/v1/keys/exchange`,
    {
      clientId: clientId,
      timestamp: timestamp,
      nonce: nonce,
    },
    {
      headers: {
        "X-CLIENT-ID": clientId,
      },
    }
  );
};

export const getConfiguredDocuments =
  (flowId, country, step, clientId, accessToken) => (dispatch) => {
    // Deliberately does NOT dispatch isLoading(): that flag also drives
    // Home.jsx's top-level view switch, which unmounts <Verification> (and
    // its in-progress currentFieldIndex/formInputs/documents state) whenever
    // it's true. DocumentTypeSelect already renders its own inline loading
    // state off `documentTypes` being unset, so it doesn't need this.
    return axios
      .get(
        `${baseUrl}/api/v1/workflows/${flowId}/document-types?countryCode=${country}&step=${step}`,
        {
          headers: {
            "X-CLIENT-ID": clientId,
            "Authorization": `Bearer ${accessToken}`,
          },
        }
      )
      .then((response) => {
        dispatch(setDocumentTypes(response.data));
      })
      .catch((err) => {
        dispatch(setError(getHttpErrorMessage(err)));
      });
  };

export const getGovernmentDatabasesByFlow =
  (flowId, clientId, accessToken) => (dispatch) => {
    // Deliberately does NOT dispatch isLoading() -- see getConfiguredDocuments
    // above for why: that flag unmounts <Verification> mid-flow via Home.jsx's
    // view switch. GovernmentDatabaseCountrySelect derives its own inline
    // loading state from `governmentDatabases` being unset instead.
    return axios
      .get(`${baseUrl}/api/v1/government-checks/flow/${flowId}`, {
        headers: {
          "X-CLIENT-ID": clientId,
          "Authorization": `Bearer ${accessToken}`,
        },
      })
      .then((response) => {
        dispatch(setGovernmentDatabases(response.data));
      })
      .catch((err) => {
        dispatch(setError(getHttpErrorMessage(err)));
      });
  };

export const sendEvent = (message) => () => {
  // React Native WebView
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
    return;
  }

  // iOS WKWebView with named message handler
  if (window.webkit?.messageHandlers?.urtentic) {
    window.webkit.messageHandlers.urtentic.postMessage(message);
    return;
  }

  // Web SDK iframe — only fire when running inside a frame AND the embedder
  // passed a validated parentOrigin query param.
  if (window.parent !== window) {
    const params = new URLSearchParams(window.location.search);
    const allowedOrigin = parseAllowedParentOrigin(params.get("parentOrigin"));
    if (allowedOrigin) {
      window.parent.postMessage(message, allowedOrigin);
      return;
    }
  }

  // Direct URL open or unrecognised context — no-op
};

export async function validateProcess(
  processType,
  documents,
  inputs,
  clientId,
  verificationId,
  accessToken
) {
  const url = `${baseUrl}/api/v1/verifications/${verificationId}/validations/${processType}`;
  const formData = new FormData();

  // Append documents if they exist
  if (documents && documents.length > 0) {
    documents.forEach((document, index) => {
      formData.append(`documents`, document.blob, document.name);
    });
  }

  // Append inputs, assumed to be an array of objects
  formData.append(
    `inputs`,
    new Blob([JSON.stringify(inputs)], { type: "application/json" })
  );

  return axios.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      "X-CLIENT-ID": clientId,
      "Authorization": `Bearer ${accessToken}`,
    },
  });
}

export async function checkDocumentQuality(
  document,
  documentType,
  clientId,
  accessToken
) {
  const url = `${baseUrl}/api/v1/documents/check-quality`;
  const formData = new FormData();
  formData.append(`document`, document, "frame.jpg");
  formData.append("document_type", documentType);

  return axios.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      "X-CLIENT-ID": clientId,
      "Authorization": `Bearer ${accessToken}`,
    },
  });
}

export async function realtimeBiometricsCheck(
  file,
  type,
  actions,
  clientId,
  accessToken
) {
  const url = `${baseUrl}/api/v1/biometrics/realtime-check`;
  const formData = new FormData();
  formData.append(`file`, file, "frame.jpg");
  formData.append("type", type);
  formData.append("actions", actions);

  return axios.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      "X-CLIENT-ID": clientId,
      "Authorization": `Bearer ${accessToken}`,
    },
  });
}

// Resolves to {sent}. sent=false means core skipped a phone code because the organisation's
// wallet couldn't pay for the SMS - the caller should move on without asking for a code.
export async function sendOTP(identity, clientId, workflowId, verificationId, accessToken, channel = "EMAIL") {
  const url = `${baseUrl}/api/v1/workflows/${workflowId}/otp`;

  const response = await axios.post(
    url,
    {
      key: identity,
      channel,
      verificationId,
    },
    {
      headers: {
        "X-CLIENT-ID": clientId,
        "Authorization": `Bearer ${accessToken}`,
      },
    }
  );
  return response.data;
}

export async function submitVerification(
  verificationId,
  documents,
  inputs,
  clientId,
  accessToken
) {
  const url = `${baseUrl}/api/v1/verifications/${verificationId}/send-inputs`;
  const formData = new FormData();

  // Append documents if they exist
  if (documents && documents.length > 0) {
    documents.forEach((document, _) => {
      formData.append(`documents`, document.blob, document.name);
    });
  }

  // Append inputs, assumed to be an array of objects
  formData.append(
    `inputs`,
    new Blob([JSON.stringify(inputs)], { type: "application/json" })
  );

  return axios.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      "X-CLIENT-ID": clientId,
      "Authorization": `Bearer ${accessToken}`,
    },
  });
}

export const checkPermission = (verification) => async (dispatch) => {
  dispatch(isLoading());
  try {
    // Check if verification.inputs contains any camera-based fields
    const requiresCamera = verification?.inputs?.some((input) =>
      ["DOCUMENT_PHOTO", "SELFIE_PHOTO", "SELFIE_VIDEO", "VIDEO_AGREEMENT"].includes(
        input.fieldName
      )
    );

    if (!requiresCamera) {
      // If no camera fields exist, set permission to "granted" immediately
      dispatch(setCameraPermission("granted"));
      return;
    }

    // Otherwise, check the actual camera permission from the browser
    const permissionStatus = await navigator.permissions.query({ name: "camera" });

    dispatch(setCameraPermission(permissionStatus.state));
  } catch (error) {
    console.error("Error checking camera permission:", error);
    dispatch(setError(getHttpErrorMessage(error)));
  }
};


export const requestCameraPermission = (flowId) => async (dispatch) => {
  dispatch(isLoading());
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    trackEvent("camera_access_error", { flowId, errorName: "unsupported_browser" });
    dispatch(setError("getUserMedia not supported on your browser!"));
    return;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    // Permission granted, and the camera stream is now available.
    trackEvent("camera_permission_granted", { flowId });
    dispatch(setCameraPermission("granted"));
    stream.getTracks().forEach((track) => {
      track.stop();
    });
  } catch (error) {
    // NotAllowedError means the user made an explicit deny choice; anything
    // else (NotFoundError, NotReadableError, OverconstrainedError...) means
    // the hardware/browser itself couldn't deliver a stream — a different
    // failure mode worth telling apart in the funnel data.
    if (error?.name === "NotAllowedError") {
      trackEvent("camera_permission_denied", { flowId });
    } else {
      trackEvent("camera_access_error", { flowId, errorName: error?.name || "unknown" });
    }
    console.error("Camera access denied", error);
    dispatch(setError(error + ""));
  }
};

export const checkMobileDevice = () => (dispatch) => {
  dispatch(isLoading());
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  dispatch(setIsMobileDevice(isMobile));
};

export const validateRedirectUrl =
  (verification, redirectUrl) => (dispatch) => {
    const redirectUrls = verification.allowedRedirectUrls || [];

    if (
      redirectUrl &&
      (redirectUrls.length === 0 || !redirectUrls.includes(redirectUrl))
    ) {
      dispatch(setError("Invalid redirect: URL not in allowed list"));
    }
  };

/**
 * Safely redirect to a URL only if it passes protocol and allowlist checks.
 * Must be called at every redirect site instead of assigning window.location.href directly.
 * @param {string} url - The redirect target from user-controlled input
 * @param {string[]} allowedRedirectUrls - Allowlist from the verified server response
 * @param {string} [flowId] - For correlating a blocked redirect back to the workflow
 */
export function safeRedirect(url, allowedRedirectUrls, flowId) {
  if (!url) return;

  // Enforce https-only to prevent javascript: and data: URI attacks
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    trackEvent("redirect_blocked", { flowId, reason: "unparseable_url" });
    return;
  }
  if (parsed.protocol !== "https:") {
    trackEvent("redirect_blocked", { flowId, reason: "non_https_protocol" });
    return;
  }

  // Must be in the server-provided allowlist (empty allowlist = no redirects permitted)
  const allowed = allowedRedirectUrls || [];
  if (allowed.length === 0 || !allowed.includes(url)) {
    trackEvent("redirect_blocked", { flowId, reason: "not_in_allowlist" });
    return;
  }

  window.location.href = url;
}

export const validateInput = (verification) => (dispatch) => {
  if(!verification.inputs.length > 0){
    trackEvent("no_processes_configured", {
      flowId: verification?.flowId,
      verificationId: verification?.verificationId,
    });
    dispatch(setError("This workflow has no required processes configured."));
  }
}

export default verificationSlice.reducer;

/**
 * Call once after the Redux store is created.
 * Intercepts every 401 response, re-authenticates using the same clientId
 * from the URL, updates credentials in the store, then retries the original
 * request. A queue prevents multiple parallel re-auth calls when several
 * requests fail simultaneously.
 */
export function setupAxiosInterceptors(store) {
  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
      error ? prom.reject(error) : prom.resolve(token);
    });
    failedQueue = [];
  };

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url?.includes("/keys/exchange")
      ) {
        // Another re-auth already in flight — queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axios(originalRequest);
          });
        }

        const params = new URLSearchParams(window.location.search);
        const clientId = params.get("clientId");
        const flowId = params.get("flowId");

        // No clientId (e.g. the ?debug=true dev view) means there is nothing to
        // re-authenticate with — the exchange could only fail, and it still
        // counts against core's per-IP rate limit on that endpoint.
        if (!clientId) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await authenticate(clientId);
          const newCredentials = response.data;
          store.dispatch(setCredentials(newCredentials));
          processQueue(null, newCredentials.accessToken);
          originalRequest.headers["Authorization"] = `Bearer ${newCredentials.accessToken}`;
          return axios(originalRequest);
        } catch (authError) {
          processQueue(authError, null);
          trackEvent("session_expired", { flowId, error: authError + "" });
          store.dispatch(setError("Session expired. Please reload and try again."));
          return Promise.reject(authError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
}
